
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMunicipalityList, updateMunicipality } from "../redux/actions/geofenceActions";
import Papa from "papaparse";
import { FiSearch, FiDownload, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import theme from "../components/shared/theme";
import {
  dropdownStyle,
  thStyle,
  tdStyle,
  paginationButtonStyle,
} from "../components/shared/themeStyles";

const Geofence = () => {
  const dispatch = useDispatch();
  const { municipalityList, loading, error } = useSelector((state) => state.geofence);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [formData, setFormData] = useState({
    municipality_name: "",
    district_name: "",
    office_name: "",
    address: "",
    latitude: "",
    longitude: "",
    photo: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipalityFilter, setSelectedMunicipalityFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  useEffect(() => {
    dispatch(fetchMunicipalityList());
  }, [dispatch]);

  const districts = [...new Set(municipalityList.map((m) => m.district_name).filter(Boolean))];
  const municipalities = [...new Set(municipalityList.map((m) => m.municipality_name).filter(Boolean))];

  const filteredList = municipalityList.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      item.municipality_name?.toLowerCase().includes(searchLower) ||
      item.district_name?.toLowerCase().includes(searchLower) ||
      item.address?.toLowerCase().includes(searchLower);

    const matchesDistrict = selectedDistrict ? item.district_name === selectedDistrict : true;
    const matchesMunicipality = selectedMunicipalityFilter ? item.municipality_name === selectedMunicipalityFilter : true;

    return matchesSearch && matchesDistrict && matchesMunicipality;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredList.length / itemsPerPage);

  const handleCSVDownload = () => {
    const rows = filteredList.map((item) => ({
      District: item.district_name || "",
      "Name of municipality": item.municipality_name || "",
      Office: item.office_name || "",
      Address: item.address || "",
      Latitude: item.latitude || "",
      Longitude: item.longitude || "",
      // Photo: item.photo || "",
      Timestamp: item.timestamp ? new Date(item.timestamp).toLocaleString("en-IN") : "-",
    }));

    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "municipality_list.csv";
    link.click();
  };

  const handleEditClick = (item) => {
    setSelectedMunicipality(item);
    setFormData({
      municipality_name: item.municipality_name || "",
      district_name: item.district_name || "",
      office_name: item.office_name || "",
      address: item.address || "",
      latitude: item.latitude || "",
      longitude: item.longitude || "",
      // photo: item.photo || "",
    });
    setEditModalOpen(true);
  };

  const handleUpdate = () => {
    if (!selectedMunicipality) return;
    const updatedData = { id: selectedMunicipality.id, ...formData };
    dispatch(updateMunicipality(updatedData)).then(() => {
      dispatch(fetchMunicipalityList()); // ✅
      setEditModalOpen(false);
      setSelectedMunicipality(null);
    });
  };

  return (
    <div className="w-full min-h-screen bg-gray-100 p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Municipality Geofence</h1>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-3 items-center bg-white p-4 rounded-lg shadow mb-6">
        <select
          value={selectedDistrict}
          onChange={(e) => {
            setSelectedDistrict(e.target.value);
            setCurrentPage(1);
          }}
          style={dropdownStyle}
        >
          <option value="">All Districts</option>
          {districts.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        <select
          value={selectedMunicipalityFilter}
          onChange={(e) => {
            setSelectedMunicipalityFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={dropdownStyle}
        >
          <option value="">All Municipalities</option>
          {municipalities.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>

        <div className="relative w-full sm:w-[260px]">
          <FiSearch className="absolute left-3 top-3 text-gray-500" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{ ...dropdownStyle, paddingLeft: theme.spacing.xl }}
          />
        </div>

        <button
          onClick={handleCSVDownload}
          style={{
            backgroundColor: theme.colors.secondary,
            color: "#fff",
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            borderRadius: theme.radius.md,
            fontWeight: theme.font.weight.medium,
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            height: "42px",
            marginLeft: "auto",
          }}
        >
          <FiDownload /> Export CSV
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-gray-700">Loading municipalities...</p>
      ) : error ? (
        <p className="text-red-600">Error: {error}</p>
      ) : currentItems.length > 0 ? (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-[900px] text-sm text-left">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th style={thStyle}>#</th>
                {/* <th style={thStyle}>Photo</th> */}
                <th style={thStyle}>Municipality</th>
                <th style={thStyle}>District</th>
                <th style={thStyle}>Office</th>
                <th style={thStyle}>Address</th>
                <th style={thStyle}>Lat</th>
                <th style={thStyle}>Lng</th>
                <th style={thStyle}>Timestamp</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentItems.map((item, index) => (
                <tr key={item.id}>
                  <td style={tdStyle}>{indexOfFirstItem + index + 1}</td>
                  {/* <td style={tdStyle}>
                    <img
                      src={item.photo || "https://img.freepik.com/free-vector/blue-circle-with-white-user_78370-4707.jpg"}
                      alt="Municipality"
                      className="h-10 w-10 rounded object-cover border border-gray-300 cursor-pointer"
                      onClick={() => {
                        setSelectedImage(item.photo);
                        setShowImageModal(true);
                      }}
                    />
                  </td> */}
                  <td style={tdStyle}>{item.municipality_name || "-"}</td>
                  <td style={tdStyle}>{item.district_name || "-"}</td>
                  <td style={tdStyle}>{item.office_name || "-"}</td>
                  <td style={tdStyle}>{item.address || "-"}</td>
                  <td style={tdStyle}>{item.latitude || "-"}</td>
                  <td style={tdStyle}>{item.longitude || "-"}</td>
                  <td style={tdStyle}>{item.timestamp ? new Date(item.timestamp).toLocaleString("en-IN") : "-"}</td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => handleEditClick(item)}
                      style={{
                        backgroundColor: theme.colors.primary,
                        color: "white",
                        borderRadius: theme.radius.sm,
                        padding: "4px 10px",
                        fontSize: theme.font.size.sm,
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex justify-between items-center p-4 border-t bg-gray-50">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={paginationButtonStyle}
            >
              <FiChevronLeft /> Previous
            </button>
            <p className="text-sm">Page {currentPage} of {totalPages}</p>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={paginationButtonStyle}
            >
              Next <FiChevronRight />
            </button>
          </div>
        </div>
      ) : (
        <p className="text-gray-600 mt-4">No municipalities found.</p>
      )}

      {/* Edit Modal */}
      {/* Edit Modal */}
{editModalOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
    <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-lg">
      <h2 className="text-xl font-semibold mb-4">Edit Coordinates</h2>

      {/* Latitude */}
      <div className="mb-4">
        <label className="block mb-1 text-sm font-medium text-gray-700">Latitude</label>
        <input
          type="number"
          value={formData.latitude}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              latitude: parseFloat(e.target.value),
            }))
          }
          step="any"
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Longitude */}
      <div className="mb-4">
        <label className="block mb-1 text-sm font-medium text-gray-700">Longitude</label>
        <input
          type="number"
          value={formData.longitude}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              longitude: parseFloat(e.target.value),
            }))
          }
          step="any"
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={() => setEditModalOpen(false)}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          onClick={() => {
            if (!selectedMunicipality) return;

            const updatedData = {
              id: selectedMunicipality.id,
              latitude: parseFloat(formData.latitude),
              longitude: parseFloat(formData.longitude),
            };

            if (isNaN(updatedData.latitude) || isNaN(updatedData.longitude)) {
              alert("Please enter valid numbers for latitude and longitude.");
              return;
            }

            dispatch(updateMunicipality(updatedData)).then(() => {
              dispatch(fetchMunicipalityList()); // ✅ Reloads table with new coordinates
              setEditModalOpen(false);
              setSelectedMunicipality(null);
            });
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Update
        </button>
      </div>
    </div>
  </div>
)}



      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg shadow-lg p-4 max-w-2xl w-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-2 right-2 text-gray-700 hover:text-red-600 text-xl font-bold"
            >
              &times;
            </button>
            <img
              src={selectedImage}
              alt="Large view"
              className="w-full max-h-[80vh] object-contain rounded"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Geofence;
